import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";

import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Formik, Form, FieldArray, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import {
  Plus,
  Trash2,
  ChevronRight,
  Save,
  Check,
  X,
  Search,
  MoreVertical,
  KeyRound,
  Pencil,
  Download,
} from "lucide-react";
import { Switch } from "../../../components/ui/switch";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { Label } from "../../../components/ui/label";
import { GlobalContext } from "../../../App";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../../components/ui/breadcrumb";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { Table } from "antd";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import axios from "axios";
import {
  change_customer_password,
  create_customer_video,
  create_customer_video_bulk,
  get_customer,
  update_customer,
  delete_customer,
  list_customer_video_portal_accounts,
  get_customer_video_accounts_to_download,
  reset_video_password,
  update_customer_video,
} from "../../../api/CustomerApi";
import { EditorProvider, useEditor, EditorContent } from "@tiptap/react"; // Added EditorProvider just in case, though Tiptap component itself might not need it directly if editor is created inside
import StarterKit from "@tiptap/starter-kit";
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
} from "lucide-react"; // Added Tiptap icons

const attributeTypes = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes/No" },
];

const countryOptions = [
  { value: "usa", label: "United States" },
  { value: "canada", label: "Canada" },
];

// Tiptap Editor setup
const extensions = [StarterKit];

const TiptapMenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 p-2 border-b border-gray-200 bg-gray-50 rounded-t-md">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("bold") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("italic") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 1 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 2 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 3 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
};

const Tiptap = ({ value, onChange }) => {
  const editor = useEditor({
    extensions,
    content: value || "<p></p>",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <div className="border rounded-md">
      <TiptapMenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose max-w-none p-4 min-h-[200px] outline-none w-full"
      />
    </div>
  );
};

const validationSchema = Yup.object().shape({
  customer_name: Yup.string().required("Customer name is required"),
  // username: Yup.string().required("Username is required"),
  // customer_address: Yup.string(),
  // customer_city: Yup.string(),
  // customer_state: Yup.string(),
  // customer_zip: Yup.string(),
  customer_country: Yup.string().required("Customer country is required"),
  // customer_number: Yup.string(),
  dedicated_phone_number: Yup.string(),
  // flow_backup_number: Yup.string(),
  // max_waiting_time: Yup.string().required("Max waiting time is required"),

  access_code: Yup.string().required("Access code is required"),
  script: Yup.string(),
  notes: Yup.string(),
  // category_code_name: Yup.string(),
  // category_code_values: Yup.array().of(Yup.string()),
  // rates: Yup.array().of(
  //   Yup.object().shape({
  //     language: Yup.string().required("Language is required"),
  //     language_code: Yup.string().required("Language code is required"),
  //     voice_rate: Yup.number()
  //       .typeError("Voice rate must be a number")
  //       .min(0, "Voice rate cannot be negative")
  //       .required("Voice rate is required"),
  //     video_rate: Yup.number()
  //       .typeError("Video rate must be a number")
  //       .min(0, "Video rate cannot be negative")
  //       .required("Video rate is required"),
  //   })
  // ),
  default_voice_rate: Yup.number()
    .typeError("Default voice rate must be a number")
    .min(0, "Default voice rate cannot be negative")
    .nullable(),
  default_video_rate: Yup.number()
    .typeError("Default video rate must be a number")
    .min(0, "Default video rate cannot be negative")
    .nullable(),
});

const EditCustomerForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const customer_id = id;
  const [loading, setLoading] = useState(false);
  const [attributeOptions, setAttributeOptions] = useState({});
  const { setToastOpen, setToastMessage, setToastSeverity, languages } =
    useContext(GlobalContext);
  const [customer, setCustomer] = useState(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState({});

  // Video portal accounts
  const [portalCreateOpen, setPortalCreateOpen] = useState(false);
  const [portalCreateSubmitting, setPortalCreateSubmitting] = useState(false);
  const [portalAccountType, setPortalAccountType] = useState("individual");
  const [portalCreateMode, setPortalCreateMode] = useState("single");
  const [portalBulkText, setPortalBulkText] = useState("");
  const [portalCreateForm, setPortalCreateForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    username: "",
    organization_name: "",
    password: "",
    confirm_password: "",
    can_view_call_history: true,
    can_change_password: true,
  });
  const [portalUsers, setPortalUsers] = useState([]);
  const [isDownloadingPortalAccounts, setIsDownloadingPortalAccounts] =
    useState(false);
  const [portalPagination, setPortalPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [portalResetOpen, setPortalResetOpen] = useState(false);
  const [portalResetUser, setPortalResetUser] = useState(null);
  const [portalResetSubmitting, setPortalResetSubmitting] = useState(false);
  const [portalEditOpen, setPortalEditOpen] = useState(false);
  const [portalEditUser, setPortalEditUser] = useState(null);
  const [portalEditForm, setPortalEditForm] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    organization_name: "",
    is_active: true,
    can_view_call_history: true,
    can_change_password: true,
  });
  const [portalEditSubmitting, setPortalEditSubmitting] = useState(false);
  const [portalDeleteOpen, setPortalDeleteOpen] = useState(false);
  const [portalDeleteUser, setPortalDeleteUser] = useState(null);
  const [portalNewPassword, setPortalNewPassword] = useState("");
  const [portalConfirmPassword, setPortalConfirmPassword] = useState("");
  const [portalPasswordError, setPortalPasswordError] = useState("");
  const [portalPasswordRequirements, setPortalPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    match: false,
  });

  const initialValues = {
    customer_name: "",
    username: "",
    customer_address: "",
    customer_city: "",
    customer_state: "",
    customer_zip: "",
    customer_country: "",
    customer_number: "",
    flow_backup_number: "",
    access_code: "",
    script: "",
    notes: "",
    category_code_name: "",
    category_code_values: "",
    attributes: [],
    default_voice_rate: 0,
    default_video_rate: 0,
    is_video_enabled: 1,
    rates: [
      {
        language: "",
        language_code: "",
        voice_rate: 0,
        video_rate: 0,
      },
    ],
  };

  useEffect(() => {
    const fetchCustomer = async () => {
      console.log(customer_id);
      if (!customer_id) return;

      setLoading(true);
      try {
        const response = await get_customer(customer_id);
        const attributes = response.attributes;
        const data = response.data;

        // Normalize attributes for UI: derive attribute_mode and ensure attribute_options is an array
        const attributesWithMode = (attributes || []).map((attr) => {
          const isInput = attr.is_input === true || attr.is_input === 1;
          const mode =
            attr.attribute_type === "select" && isInput
              ? "list"
              : isInput
              ? "input"
              : "text";
          const optionsArray = Array.isArray(attr.attribute_options)
            ? attr.attribute_options
            : typeof attr.attribute_options === "string" &&
              attr.attribute_options.length > 0
            ? attr.attribute_options.split(",").map((s) => s.trim())
            : [];
          return {
            ...attr,
            attribute_mode: mode,
            attribute_options: optionsArray,
          };
        });

        setAttributeOptions(attributesWithMode);
        // console.log(attributes);
        // Transform the rates data if it exists, or initialize with default values
        const rates =
          response.rates && response.rates.length > 0
            ? response.rates.map((rate) => ({
                language: rate.language || "",
                language_code: rate.language_code || "",
                voice_rate: rate.voice_rate || 0,
                video_rate: rate.video_rate || 0,
              }))
            : [
                {
                  language: "",
                  language_code: "",
                  voice_rate:
                    (data && (data.default_voice_rate ?? data.voice_rate)) || 0,
                  video_rate:
                    (data && (data.default_video_rate ?? data.video_rate)) || 0,
                },
              ];
        console.log(data);
        setCustomer({
          ...data,
          script: data.script || "",
          notes: data.notes || "",
          category_code_name: data.category_code_name || "",
          category_code_values: data.category_code_values || "",
          attributes: attributesWithMode || [],
          rates: rates,
          default_voice_rate:
            (data && (data.default_voice_rate ?? data.voice_rate)) || 0,
          default_video_rate:
            (data && (data.default_video_rate ?? data.video_rate)) || 0,
          is_video_enabled: data.is_video_enabled,
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching customer:", error);
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage("Failed to load customer data. Please try again.");
        setLoading(false);
      }
    };

    fetchCustomer();
    fetchPortalAccounts(1, 10);
  }, [customer_id]);

  const downloadPortalAccountsCSV = async () => {
    if (!customer_id) return;
    setIsDownloadingPortalAccounts(true);
    try {
      const response = await get_customer_video_accounts_to_download(
        customer_id
      );
      const data = response?.data || [];

      if (!data.length) {
        setToastMessage("No video portal accounts to download.");
        setToastOpen(true);
        setToastSeverity("warning");
        return;
      }

      const columns = [
        { header: "Username", value: (row) => row.username },
        { header: "First Name", value: (row) => row.first_name },
        { header: "Last Name", value: (row) => row.last_name },
        { header: "Email", value: (row) => row.email },
        { header: "Phone Number", value: (row) => row.phone_number },
        { header: "Organization", value: (row) => row.organization_name },
        { header: "Account Type", value: (row) => row.account_type },
        {
          header: "Status",
          value: (row) =>
            Number(row.is_active) === 1 ? "Active" : "Inactive",
        },
        { header: "Created At", value: (row) => row.created_at },
      ];

      const toCsvCell = (value) =>
        `"${String(value ?? "").replace(/"/g, '""')}"`;
      const header = columns.map((col) => toCsvCell(col.header)).join(",");
      const csvRows = data.map((row) =>
        columns.map((col) => toCsvCell(col.value(row))).join(",")
      );
      // BOM so Excel opens accented names as UTF-8
      const csvContent = "\uFEFF" + [header, ...csvRows].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const safeName = (customer?.customer_name || "customer")
        .replace(/[^a-zA-Z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "");
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `video_accounts_${safeName || "customer"}_${format(
          new Date(),
          "yyyy-MM-dd"
        )}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Deferred so Safari has started the download before the URL is released
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error("Error downloading video portal accounts:", error);
      setToastMessage(
        error.response?.data?.message ||
          "Failed to download video portal accounts."
      );
      setToastOpen(true);
      setToastSeverity("error");
    } finally {
      setIsDownloadingPortalAccounts(false);
    }
  };

  const fetchPortalAccounts = async (page = 1, pageSize = 10) => {
    if (!customer_id) return;
    try {
      const response = await list_customer_video_portal_accounts(
        customer_id,
        page,
        pageSize
      );
      const data = response?.data ?? response;
      const items = data?.items ?? [];
      const meta = data?.meta ?? {};
      setPortalUsers(items);
      setPortalPagination({
        current: meta.current_page ?? page,
        pageSize: meta.per_page ?? pageSize,
        total: meta.total ?? 0,
      });
    } catch (err) {
      console.error("Error fetching portal accounts:", err);
    }
  };

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
    match: false,
  });

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password)) {
      return "Password must contain uppercase, lowercase, number and special character";
    }
    return "";
  };

  const checkPasswordRequirements = (password, confirmPwd) => {
    setPasswordRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password),
      match: password === confirmPwd && password !== "",
    });
  };

  const handleResetPassword = async () => {
    // Validate password
    const error = validatePassword(newPassword);
    if (error) {
      setPasswordError(error);
      return;
    }

    // Check if passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (!id) {
      setToastOpen(true);
      setToastSeverity("warning");
      setToastMessage("Customer ID is missing.");
      return;
    }

    try {
      await change_customer_password(id, newPassword, confirmPassword);

      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Password reset successfully.");
      setResetPasswordOpen(false);
      setNewPassword("");
      setConfirmPassword("");
      setPasswordError("");
      setPasswordRequirements({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        match: false,
      });
    } catch (error) {
      console.error("Error resetting password:", error);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        error.response?.data?.message ||
          "Failed to reset password. Please try again."
      );
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);

      // Format the submission values
      const submissionValues = {
        ...values,
        is_video_enabled: values.is_video_enabled,
        attributes: (values.attributes || []).map((attr) => {
          const mode = attr.attribute_mode || "text";
          const isList = mode === "list";
          const isInput = mode === "input" || mode === "list";
          const optionsArray = Array.isArray(attr.attribute_options)
            ? attr.attribute_options.filter(
                (v) => typeof v === "string" && v.trim() !== ""
              )
            : [];
          return {
            ...attr,
            attribute_type: isList ? "select" : "text",
            is_input: isInput,
            attribute_options: isList ? optionsArray : [],
          };
        }),
        notes: values.notes || "",
        category_code_values: Array.isArray(values.category_code_values)
          ? values.category_code_values.join(",")
          : values.category_code_values,

        // Ensure rates is an array and filter out any invalid entries
        rates: (values.rates || [])
          .filter(
            (rate) =>
              rate.language_code &&
              rate.voice_rate !== undefined &&
              rate.video_rate !== undefined
          )
          .map((rate) => ({
            language: rate.language,
            language_code: rate.language_code,
            voice_rate: parseFloat(rate.voice_rate) || 0,
            video_rate: parseFloat(rate.video_rate) || 0,
          })),
      };
      console.log(submissionValues);
      await update_customer(submissionValues);
      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Customer updated successfully.");
      navigate(`/admin/customers/edit/${id}`);
    } catch (error) {
      console.error("Error updating customer:", error);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        error.response?.data?.message ||
          "Failed to update customer. Please try again."
      );
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const handleAttributeTypeChange = (value, index, setFieldValue, values) => {
    setFieldValue(`attributes[${index}].attribute_type`, value);
    if (!["select", "multiselect"].includes(value)) {
      setFieldValue(`attributes[${index}].attribute_options`, "");
      setAttributeOptions((prev) => {
        const updated = { ...prev };
        delete updated[values.attributes[index].attribute_name];
        return updated;
      });
    }
  };

  const handleAttributeOptionsChange = (
    value,
    index,
    setFieldValue,
    values
  ) => {
    setFieldValue(`attributes[${index}].attribute_options`, value);
    if (values.attributes[index].attribute_name) {
      setAttributeOptions((prev) => ({
        ...prev,
        [values.attributes[index].attribute_name]: value
          .split(",")
          .map((opt) => opt.trim()),
      }));
    }
  };

  // Function to add a new attribute
  const addAttribute = (push) => {
    push({
      attribute_name: "",
      attribute_label: "",
      attribute_type: "text",
      attribute_options: "",
      is_required: false,
      is_input_type: false,
      value: "",
    });
  };

  const validatePortalPassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(password))
      return "Password must contain uppercase, lowercase, number and special character";
    return "";
  };

  const checkPortalPasswordRequirements = (password, confirmPwd) => {
    setPortalPasswordRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[@$!%*?&]/.test(password),
      match: password === confirmPwd && password !== "",
    });
  };

  const organizationOptions = [
    ...(customer?.customer_name
      ? [{ value: "current", label: customer.customer_name }]
      : []),
    { value: "org-a", label: "Organization A" },
    { value: "org-b", label: "Organization B" },
  ];

  const handlePortalCreateOpen = () => {
    setPortalAccountType("individual");
    setPortalCreateMode("single");
    setPortalBulkText("");
    setPortalCreateForm({
      first_name: "",
      last_name: "",
      email: "",
      username: "",
      organization_name: "",
      password: "",
      confirm_password: "",
      can_view_call_history: true,
      can_change_password: true,
    });
    setPortalCreateOpen(true);
  };

  const orgValues = organizationOptions.map((o) => o.value);

  /**
   * Builds the payload to POST when creating video portal accounts.
   * Spec: see docs/PORTAL_ACCOUNTS_PAYLOAD.md
   *
   * @param {string} customerId
   * @param {"single"|"bulk"} mode
   * @param {"shared"|"individual"} accountType
   * @param {object} data - For single: form fields. For bulk: { accounts: [...] }
   * @returns {object} Payload for your backend.
   */
  const buildPortalCreatePayload = (customerId, mode, accountType, data) => {
    const accountFlags = {
      can_view_call_history: data.can_view_call_history !== false,
      can_change_password: data.can_change_password !== false,
    };
    if (mode === "single") {
      const base = { customer_id: customerId, type: accountType, ...accountFlags };
      if (accountType === "shared")
        return { ...base, username: data.username, password: data.password };
      return {
        ...base,
        first_name: data.first_name,
        last_name: data.last_name,
        account_email: data.email,
        username: data.username,
        organization_name: data.organization_name,
        password: data.password,
      };
    }
    return {
      customer_id: customerId,
      ...accountFlags,
      accounts: data.accounts.map((a) =>
        accountType === "shared"
          ? { type: "shared", username: a.username, password: a.password }
          : {
              type: "individual",
              first_name: a.first_name,
              last_name: a.last_name,
              account_email: a.email,
              username: a.username,
              organization_name: a.organization_name,
              password: a.password,
            }
      ),
    };
  };

  const validateCreatePassword = (password, confirmPassword) => {
    if (!password?.trim()) return "Password is required.";
    if (!confirmPassword?.trim()) return "Confirm password is required.";
    if (password !== confirmPassword) return "Passwords do not match.";
    return validatePortalPassword(password) || null;
  };

  const handlePortalCreate = async () => {
    if (!customer_id) {
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        "Customer ID is missing. Cannot create video portal accounts."
      );
      return;
    }
    if (portalCreateMode === "bulk") {
      const lines = portalBulkText
        .split(/\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (lines.length === 0) {
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage("Enter at least one line for bulk insert.");
        return;
      }
      if (portalAccountType === "shared") {
        const newShared = [];
        const errs = [];
        lines.forEach((line, idx) => {
          const parts = line.split(/[\t,]/).map((s) => s.trim());
          if (parts.length < 2) {
            errs.push(`Line ${idx + 1}: username,password required.`);
            return;
          }
          const [username, password] = parts;
          if (!username || !password) {
            errs.push(`Line ${idx + 1}: username and password required.`);
            return;
          }
          const pwErr = validatePortalPassword(password);
          if (pwErr) {
            errs.push(`Line ${idx + 1}: ${pwErr}`);
            return;
          }
          newShared.push({
            id: `shared-${Date.now()}-${idx}-${Math.random()
              .toString(36)
              .slice(2)}`,
            type: "shared",
            username,
            password,
          });
        });
        if (errs.length > 0) {
          setToastOpen(true);
          setToastSeverity("error");
          setToastMessage(
            errs.slice(0, 3).join(" ") +
              (errs.length > 3 ? ` (+${errs.length - 3} more)` : "")
          );
          return;
        }
        if (newShared.length === 0) {
          setToastOpen(true);
          setToastSeverity("error");
          setToastMessage("Enter at least one username,password per line.");
          return;
        }
        const payload = buildPortalCreatePayload(
          customer_id,
          "bulk",
          "shared",
          {
            accounts: newShared,
            can_view_call_history: portalCreateForm.can_view_call_history,
            can_change_password: portalCreateForm.can_change_password,
          }
        );
        setPortalCreateSubmitting(true);
        try {
          await create_customer_video_bulk({ ...payload, customer_id });
          setPortalUsers((prev) => [...prev, ...newShared]);
          setPortalBulkText("");
          setPortalCreateOpen(false);
          setToastOpen(true);
          setToastSeverity("success");
          setToastMessage(
            `${newShared.length} shared video portal account(s) created.`
          );
        } catch (err) {
          setToastOpen(true);
          setToastSeverity("error");
          setToastMessage(
            err.response?.data?.message ||
              "Failed to create video portal accounts."
          );
        } finally {
          setPortalCreateSubmitting(false);
        }
        return;
      }
      const newIndividuals = [];
      const errors = [];
      lines.forEach((line, idx) => {
        const parts = line.split(/[\t,]/).map((s) => s.trim());
        if (parts.length < 6) {
          errors.push(
            `Line ${
              idx + 1
            }: need 6 columns (firstName, lastName, email, username, organization, password).`
          );
          return;
        }
        const [firstName, lastName, email, username, organization, password] =
          parts;
        if (
          !firstName ||
          !lastName ||
          !email ||
          !username ||
          !organization ||
          !password
        ) {
          errors.push(`Line ${idx + 1}: all fields required.`);
          return;
        }
        const pwErr = validatePortalPassword(password);
        if (pwErr) {
          errors.push(`Line ${idx + 1}: ${pwErr}`);
          return;
        }
        newIndividuals.push({
          id: `ind-${Date.now()}-${idx}-${Math.random().toString(36).slice(2)}`,
          type: "individual",
          firstName,
          lastName,
          email,
          username,
          organization,
          password,
        });
      });
      if (errors.length > 0) {
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage(
          errors.slice(0, 3).join(" ") +
            (errors.length > 3 ? ` (+${errors.length - 3} more)` : "")
        );
        return;
      }
      const payload = buildPortalCreatePayload(
        customer_id,
        "bulk",
        "individual",
        {
          accounts: newIndividuals,
          can_view_call_history: portalCreateForm.can_view_call_history,
          can_change_password: portalCreateForm.can_change_password,
        }
      );
      setPortalCreateSubmitting(true);
      try {
        await create_customer_video_bulk({ ...payload, customer_id });
        setPortalUsers((prev) => [...prev, ...newIndividuals]);
        setPortalBulkText("");
        setPortalCreateOpen(false);
        setToastOpen(true);
        setToastSeverity("success");
        setToastMessage(
          `${newIndividuals.length} individual video portal account(s) created.`
        );
      } catch (err) {
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage(
          err.response?.data?.message ||
            "Failed to create video portal accounts."
        );
      } finally {
        setPortalCreateSubmitting(false);
      }
      return;
    }

    if (portalAccountType === "shared") {
      const username = portalCreateForm.username?.trim();
      if (!username) {
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage("Username is required for shared accounts.");
        return;
      }
      const pwErr = validateCreatePassword(
        portalCreateForm.password,
        portalCreateForm.confirm_password
      );
      if (pwErr) {
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage(pwErr);
        return;
      }
      const password = portalCreateForm.password.trim();
      const payload = buildPortalCreatePayload(
        customer_id,
        "single",
        "shared",
        {
          username,
          password,
          can_view_call_history: portalCreateForm.can_view_call_history,
          can_change_password: portalCreateForm.can_change_password,
        }
      );
      setPortalCreateSubmitting(true);
      try {
        await create_customer_video({ ...payload, customer_id });
        setPortalUsers((prev) => [
          ...prev,
          {
            id: `shared-${Date.now()}`,
            type: "shared",
            username,
            password,
          },
        ]);
        setPortalCreateForm((prev) => ({
          ...prev,
          username: "",
          password: "",
          confirm_password: "",
        }));
        setPortalCreateOpen(false);
        setToastOpen(true);
        setToastSeverity("success");
        setToastMessage("Shared video portal account created.");
      } catch (err) {
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage(
          err.response?.data?.message ||
            "Failed to create video portal account."
        );
      } finally {
        setPortalCreateSubmitting(false);
      }
      return;
    }

    const {
      first_name,
      last_name,
      email,
      username,
      organization_name,
      password,
      confirm_password,
    } = portalCreateForm;
    // First name, last name, email, username required; organization is optional
    if (
      !first_name?.trim() ||
      !last_name?.trim() ||
      !email?.trim() ||
      !username?.trim()
    ) {
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        "Please fill in first name, last name, email, and username. Organization is optional."
      );
      return;
    }
    const pwErr = validateCreatePassword(password, confirm_password);
    if (pwErr) {
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(pwErr);
      return;
    }
    const pw = password.trim();
    const payload = buildPortalCreatePayload(
      customer_id,
      "single",
      "individual",
      {
        first_name: first_name.trim(),
        last_name: last_name.trim(),
        email: email.trim(),
        username: username.trim(),
        organization_name: organization_name?.trim() || undefined,
        password: pw,
        can_view_call_history: portalCreateForm.can_view_call_history,
        can_change_password: portalCreateForm.can_change_password,
      }
    );
    setPortalCreateSubmitting(true);
    try {
      await create_customer_video({ ...payload, customer_id });
      setPortalUsers((prev) => [
        ...prev,
        {
          id: `ind-${Date.now()}`,
          type: "individual",
          first_name: first_name.trim(),
          last_name: last_name.trim(),
          email: email.trim(),
          username: username.trim(),
          organization_name,
          password: pw,
        },
      ]);
      setPortalCreateForm({
        first_name: "",
        last_name: "",
        email: "",
        username: "",
        organization_name: "",
        password: "",
        confirm_password: "",
        can_view_call_history: true,
        can_change_password: true,
      });
      setPortalCreateOpen(false);
      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Individual video portal account created.");
    } catch (err) {
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        err.response?.data?.message || "Failed to create video portal account."
      );
    } finally {
      setPortalCreateSubmitting(false);
    }
  };

  const handlePortalResetOpen = (user) => {
    setPortalResetUser(user);
    setPortalNewPassword("");
    setPortalConfirmPassword("");
    setPortalPasswordError("");
    setPortalPasswordRequirements({
      length: false,
      uppercase: false,
      lowercase: false,
      number: false,
      special: false,
      match: false,
    });
    setPortalResetOpen(true);
  };

  const handlePortalResetPassword = async () => {
    const err = validatePortalPassword(portalNewPassword);
    if (err) {
      setPortalPasswordError(err);
      return;
    }
    if (portalNewPassword !== portalConfirmPassword) {
      setPortalPasswordError("Passwords do not match");
      return;
    }
    if (!portalResetUser?.customer_video_account_id || !customer_id) return;
    try {
      setPortalResetSubmitting(true);
      setPortalPasswordError("");
      await reset_video_password(
        customer_id,
        portalResetUser.customer_video_account_id,
        portalNewPassword,
        portalConfirmPassword
      );
      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Video portal account password reset successfully.");
      setPortalResetOpen(false);
      setPortalResetUser(null);
      setPortalNewPassword("");
      setPortalConfirmPassword("");
    } catch (error) {
      console.error("Error resetting video portal password:", error);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        error.response?.data?.message ||
          "Failed to reset video portal password. Please try again."
      );
    } finally {
      setPortalResetSubmitting(false);
    }
  };

  const handlePortalEditOpen = (user) => {
    setPortalEditUser(user);
    setPortalEditForm({
      username: user?.username ?? "",
      first_name: user?.first_name ?? "",
      last_name: user?.last_name ?? "",
      email: user?.email ?? "",
      phone_number: user?.phone_number ?? "",
      organization_name: user?.organization_name ?? user?.organization ?? "",
      is_active: user?.is_active === 1 || user?.is_active === true,
      can_view_call_history:
        user?.can_view_call_history !== 0 &&
        user?.can_view_call_history !== false,
      can_change_password:
        user?.can_change_password !== 0 &&
        user?.can_change_password !== false,
    });
    setPortalEditOpen(true);
  };

  const handlePortalEditSave = async () => {
    if (!portalEditUser?.customer_video_account_id || !customer_id) return;
    if (portalEditUser?.account_type === "individual") {
      const email = portalEditForm.email?.trim();
      if (!email) {
        setToastOpen(true);
        setToastSeverity("error");
        setToastMessage("Email is required for individual accounts.");
        return;
      }
    }
    try {
      setPortalEditSubmitting(true);
      const payload = {
        username: portalEditForm.username?.trim() || undefined,
        first_name: portalEditForm.first_name?.trim() || undefined,
        last_name: portalEditForm.last_name?.trim() || undefined,
        phone_number: portalEditForm.phone_number?.trim() || undefined,
        organization_name:
          portalEditForm.organization_name?.trim() || undefined,
        is_active: portalEditForm.is_active ? 1 : 0,
        can_view_call_history: portalEditForm.can_view_call_history ? 1 : 0,
        can_change_password: portalEditForm.can_change_password ? 1 : 0,
      };
      if (portalEditUser?.account_type === "individual") {
        payload.account_email = portalEditForm.email?.trim();
      }
      await update_customer_video(
        customer_id,
        portalEditUser.customer_video_account_id,
        payload
      );
      setPortalUsers((prev) =>
        prev.map((u) =>
          u.customer_video_account_id ===
          portalEditUser.customer_video_account_id
            ? {
                ...u,
                username: portalEditForm.username?.trim() || u.username,
                first_name: portalEditForm.first_name?.trim() ?? u.first_name,
                last_name: portalEditForm.last_name?.trim() ?? u.last_name,
                email: portalEditForm.email?.trim() ?? u.email,
                phone_number:
                  portalEditForm.phone_number?.trim() ?? u.phone_number,
                organization_name:
                  portalEditForm.organization_name?.trim() ??
                  u.organization_name,
                is_active: portalEditForm.is_active ? 1 : 0,
                can_view_call_history: portalEditForm.can_view_call_history
                  ? 1
                  : 0,
                can_change_password: portalEditForm.can_change_password
                  ? 1
                  : 0,
              }
            : u
        )
      );
      setToastOpen(true);
      setToastSeverity("success");
      setToastMessage("Video portal account updated.");
      setPortalEditOpen(false);
      setPortalEditUser(null);
    } catch (error) {
      console.error("Error updating video portal account:", error);
      setToastOpen(true);
      setToastSeverity("error");
      setToastMessage(
        error.response?.data?.message ||
          "Failed to update video portal account. Please try again."
      );
    } finally {
      setPortalEditSubmitting(false);
    }
  };

  const handlePortalDelete = (user) => {
    setPortalDeleteUser(user);
    setPortalDeleteOpen(true);
  };

  const handlePortalDeleteConfirm = () => {
    setPortalUsers((prev) =>
      prev.filter(
        (u) =>
          u.customer_video_account_id !==
          portalDeleteUser.customer_video_account_id
      )
    );
    setPortalDeleteOpen(false);
    setPortalDeleteUser(null);
    setToastOpen(true);
    setToastSeverity("success");
    setToastMessage("Video portal account deleted.");
  };

  if (loading && !customer) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading customer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center justify-between">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate("/admin")}>
                Admin
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate("/admin/customers")}>
                Customers
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>Edit Customer</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Top Action Buttons */}
        <div className="fixed bottom-0 left-0 right-0 w-full bg-white p-4 flex justify-between items-center mt-6 border-t border-gray-200 shadow-lg z-[1000]">
          <div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={loading}
              className="border-red-500 text-red-500 hover:bg-red-50 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Customer
            </Button>
          </div>
          <div className="flex space-x-4">
            <Dialog
              open={resetPasswordOpen}
              onOpenChange={setResetPasswordOpen}
            >
              <DialogTrigger asChild>
                <Button type="button" variant="outline" disabled={loading}>
                  Reset Password
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Reset Password</DialogTitle>
                  <DialogDescription>
                    Enter a new password for this customer account.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setNewPassword(newValue);
                        checkPasswordRequirements(newValue, confirmPassword);
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setConfirmPassword(newValue);
                        checkPasswordRequirements(newPassword, newValue);
                      }}
                    />
                  </div>
                  <div className="space-y-2 mt-2 text-sm">
                    <p className="font-medium">Password requirements:</p>
                    <ul className="space-y-1 pl-2">
                      <li className="flex items-center">
                        {passwordRequirements.length ? (
                          <Check className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 mr-2 text-red-500" />
                        )}
                        At least 8 characters
                      </li>
                      <li className="flex items-center">
                        {passwordRequirements.uppercase ? (
                          <Check className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 mr-2 text-red-500" />
                        )}
                        At least one uppercase letter
                      </li>
                      <li className="flex items-center">
                        {passwordRequirements.lowercase ? (
                          <Check className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 mr-2 text-red-500" />
                        )}
                        At least one lowercase letter
                      </li>
                      <li className="flex items-center">
                        {passwordRequirements.number ? (
                          <Check className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 mr-2 text-red-500" />
                        )}
                        At least one number
                      </li>
                      <li className="flex items-center">
                        {passwordRequirements.special ? (
                          <Check className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 mr-2 text-red-500" />
                        )}
                        At least one special character (@$!%*?&)
                      </li>
                      <li className="flex items-center">
                        {passwordRequirements.match ? (
                          <Check className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 mr-2 text-red-500" />
                        )}
                        Passwords match
                      </li>
                    </ul>
                  </div>
                  {passwordError && (
                    <div className="text-sm text-red-500 mt-2">
                      {passwordError}
                    </div>
                  )}
                  <DialogFooter className="pt-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setResetPasswordOpen(false);
                        setNewPassword("");
                        setConfirmPassword("");
                        setPasswordError("");
                        setPasswordRequirements({
                          length: false,
                          uppercase: false,
                          lowercase: false,
                          number: false,
                          special: false,
                          match: false,
                        });
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="button" onClick={handleResetPassword}>
                      Reset Password
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>
            <Button
              type="submit" // This button will now trigger the Formik submission
              form="editCustomerForm" // Associate with the Formik form ID
              disabled={loading} // Disable if loading
            >
              <Save className="h-4 w-4" />
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      <Formik
        initialValues={customer || initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form id="editCustomerForm">
            <Tabs defaultValue="customer-info" className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2 mb-4">
                <TabsTrigger value="customer-info">
                  Customer Information
                </TabsTrigger>
                <TabsTrigger value="video-portal">
                  Video portal accounts
                </TabsTrigger>
              </TabsList>
              <TabsContent value="customer-info" className="space-y-2 mt-0">
                {/* Customer Information */}
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle>Customer Information</CardTitle>
                    <CardDescription>Basic customer details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="customer_name">Customer Name *</Label>
                        <Field
                          as={Input}
                          id="customer_name"
                          name="customer_name"
                          placeholder="Enter customer name"
                        />
                        <ErrorMessage
                          name="customer_name"
                          component="div"
                          className="text-sm text-red-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="username">Username *</Label>
                        <Field
                          as={Input}
                          id="username"
                          name="username"
                          placeholder="Enter username"
                        />
                        <ErrorMessage
                          name="username"
                          component="div"
                          className="text-sm text-red-500"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="access_code">Access Code *</Label>
                        <Field
                          as={Input}
                          id="access_code"
                          name="access_code"
                          placeholder="Enter access code"
                        />
                        <ErrorMessage
                          name="access_code"
                          component="div"
                          className="text-sm text-red-500"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Contact Information */}
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle>Contact Information</CardTitle>
                    <CardDescription>
                      Phone numbers and communication details
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-1">
                        <Label htmlFor="customer_number">Phone Number</Label>
                        <Field
                          as={Input}
                          id="customer_number"
                          name="customer_number"
                          placeholder="Enter phone number"
                        />
                        <ErrorMessage
                          name="customer_number"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>
                      <div></div>
                      <div className="space-y-1">
                        <Label htmlFor="dedicated_phone_number">
                          Dedicated Phone Number
                        </Label>
                        <Field
                          as={Input}
                          id="dedicated_phone_number"
                          name="dedicated_phone_number"
                          placeholder="Enter Zoom dedicated phone number"
                        />
                        <ErrorMessage
                          name="dedicated_phone_number"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="flow_backup_number">
                          Backup Phone Number
                        </Label>
                        <Field
                          as={Input}
                          id="flow_backup_number"
                          name="flow_backup_number"
                          placeholder="Enter backup phone number"
                        />
                        <ErrorMessage
                          name="flow_backup_number"
                          component="div"
                          className="text-xs text-red-500 font-light"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Address Information */}
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle>Address Information</CardTitle>
                    <CardDescription>Customer location details</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="customer_country">Country *</Label>
                        <Field name="customer_country">
                          {({ field, form }) => {
                            console.log("Current country value:", field.value);
                            return (
                              <Select
                                value={field.value || ""}
                                onValueChange={(value) => {
                                  form.setFieldValue(field.name, value);
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select country">
                                    {field.value
                                      ? countryOptions.find(
                                          (c) => c.value === field.value
                                        )?.label
                                      : "Select country"}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {countryOptions.map((country) => (
                                    <SelectItem
                                      key={country.value}
                                      value={country.value}
                                    >
                                      {country.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          }}
                        </Field>
                        <ErrorMessage
                          name="customer_country"
                          component="div"
                          className="text-sm text-red-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="customer_address">Address</Label>
                        <Field
                          as={Input}
                          id="customer_address"
                          name="customer_address"
                          placeholder="Enter address"
                        />
                        <ErrorMessage
                          name="customer_address"
                          component="div"
                          className="text-sm text-red-500"
                        />
                      </div>

                      <div className="grid gap-4 md:grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor="customer_city">City</Label>
                          <Field
                            as={Input}
                            id="customer_city"
                            name="customer_city"
                            placeholder="Enter city"
                          />
                          <ErrorMessage
                            name="customer_city"
                            component="div"
                            className="text-sm text-red-500"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="customer_state">State/Province</Label>
                          <Field
                            as={Input}
                            id="customer_state"
                            name="customer_state"
                            placeholder="Enter state/province"
                          />
                          <ErrorMessage
                            name="customer_state"
                            component="div"
                            className="text-sm text-red-500"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="customer_zip">ZIP/Postal Code</Label>
                          <Field
                            as={Input}
                            id="customer_zip"
                            name="customer_zip"
                            placeholder="Enter ZIP/Postal code"
                          />
                          <ErrorMessage
                            name="customer_zip"
                            component="div"
                            className="text-sm text-red-500"
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Language Rates */}
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle>Rates</CardTitle>
                    <CardDescription>
                      Set rates per language for voice and video calls
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 pr-4 mb-4">
                      <div className="space-y-1">
                        <Label htmlFor="default_voice_rate" className="text-xs">
                          Default Voice Rate ($/min)
                        </Label>
                        <div className="w-40">
                          <Field
                            as={Input}
                            id="default_voice_rate"
                            name="default_voice_rate"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="default_video_rate" className="text-xs">
                          Default Video Rate ($/min)
                        </Label>
                        <div className="w-40">
                          <Field
                            as={Input}
                            id="default_video_rate"
                            name="default_video_rate"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>
                    <FieldArray name="rates">
                      {({ push, remove, form }) => (
                        <div className="space-y-4">
                          {form.values.rates && form.values.rates.length > 0 ? (
                            form.values.rates.map((rate, index) => (
                              <div
                                key={index}
                                className="p-4 border border-gray-200 rounded-lg space-y-4"
                              >
                                <div className="flex justify-between items-center">
                                  <h4 className="font-medium">
                                    {rate.language} Rate
                                  </h4>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      if (form.values.rates.length > 1) {
                                        remove(index);
                                      }
                                    }}
                                    disabled={form.values.rates.length <= 1}
                                    className="text-red-500 hover:text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>

                                <div className="grid gap-4 md:grid-cols-3">
                                  <div className="space-y-2">
                                    <Label htmlFor={`rates.${index}.language`}>
                                      Language *
                                    </Label>
                                    <Field
                                      name={`rates.${index}.language_code`}
                                    >
                                      {({ field, form }) => (
                                        <Popover
                                          open={
                                            popoverOpen[`rate-${index}`] ||
                                            false
                                          }
                                          onOpenChange={(open) =>
                                            setPopoverOpen((prev) => ({
                                              ...prev,
                                              [`rate-${index}`]: open,
                                            }))
                                          }
                                        >
                                          <PopoverTrigger asChild>
                                            <Button
                                              variant="outline"
                                              role="combobox"
                                              className="w-full justify-between"
                                              aria-expanded={
                                                popoverOpen[`rate-${index}`]
                                              }
                                            >
                                              {field.value
                                                ? languages.find(
                                                    (lang) =>
                                                      lang.name ==
                                                      values.rates[index]
                                                        .language
                                                  )?.name || "Select language"
                                                : "Select language"}
                                              <ChevronRight className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-[300px] p-0">
                                            <Command>
                                              <CommandInput placeholder="Search language..." />
                                              <CommandList>
                                                <CommandEmpty>
                                                  No language found.
                                                </CommandEmpty>
                                                <CommandGroup>
                                                  {languages.map((language) => (
                                                    <CommandItem
                                                      key={language.code}
                                                      value={language.name}
                                                      onSelect={() => {
                                                        form.setFieldValue(
                                                          `rates.${index}.language`,
                                                          language.name
                                                        );
                                                        form.setFieldValue(
                                                          `rates.${index}.language_code`,
                                                          language.code
                                                        );
                                                        // Close the popover after selection
                                                        setPopoverOpen(
                                                          (prev) => ({
                                                            ...prev,
                                                            [`rate-${index}`]: false,
                                                          })
                                                        );
                                                      }}
                                                      className="cursor-pointer"
                                                    >
                                                      <Check
                                                        className={`mr-2 h-4 w-4 ${
                                                          values.rates[index]
                                                            .language ===
                                                          language.name
                                                            ? "opacity-100"
                                                            : "opacity-0"
                                                        }`}
                                                      />
                                                      {language.name} (
                                                      {language.code})
                                                    </CommandItem>
                                                  ))}
                                                </CommandGroup>
                                              </CommandList>
                                            </Command>
                                          </PopoverContent>
                                        </Popover>
                                      )}
                                    </Field>
                                    <ErrorMessage
                                      name={`rates.${index}.language_code`}
                                      component="div"
                                      className="text-sm text-red-500"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <Label
                                      htmlFor={`rates.${index}.voice_rate`}
                                    >
                                      Voice Rate ($/min) *
                                    </Label>
                                    <Field
                                      as={Input}
                                      id={`rates.${index}.voice_rate`}
                                      name={`rates.${index}.voice_rate`}
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="0.00"
                                    />
                                    <ErrorMessage
                                      name={`rates.${index}.voice_rate`}
                                      component="div"
                                      className="text-sm text-red-500"
                                    />
                                  </div>

                                  <div className="space-y-2">
                                    <Label
                                      htmlFor={`rates.${index}.video_rate`}
                                    >
                                      Video Rate ($/min) *
                                    </Label>
                                    <Field
                                      as={Input}
                                      id={`rates.${index}.video_rate`}
                                      name={`rates.${index}.video_rate`}
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      placeholder="0.00"
                                    />
                                    <ErrorMessage
                                      name={`rates.${index}.video_rate`}
                                      component="div"
                                      className="text-sm text-red-500"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-6 border-2 border-dashed rounded-lg">
                              <p className="text-gray-500">
                                No language rates added yet.
                              </p>
                            </div>
                          )}

                          <div className="flex justify-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                push({
                                  language: "",
                                  language_code: "",
                                  voice_rate: 0,
                                  video_rate: 0,
                                })
                              }
                              className="mt-2"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Language Rate
                            </Button>
                          </div>
                        </div>
                      )}
                    </FieldArray>
                  </CardContent>
                </Card>

                {/* Category Codes */}
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle>Category Codes</CardTitle>
                    <CardDescription>
                      Assign category code name and values
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="category_code_name">
                          Category Code Name
                        </Label>
                        <Field
                          as={Input}
                          id="category_code_name"
                          name="category_code_name"
                          placeholder="Enter category code name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Category Code Values</Label>
                        <FieldArray name="category_code_values">
                          {({ form }) => {
                            const splitValues =
                              form.values.category_code_values
                                ?.split(",")
                                .filter(() => true) || [];

                            return (
                              <div className="space-y-3">
                                {splitValues.length > 0 ? (
                                  <div className="space-y-2">
                                    {splitValues.map((value, index) => (
                                      <div
                                        key={index}
                                        className="flex items-center gap-2"
                                      >
                                        <Input
                                          value={value}
                                          onChange={(e) => {
                                            const updated = [...splitValues];
                                            updated[index] = e.target.value;
                                            form.setFieldValue(
                                              "category_code_values",
                                              updated.join(",")
                                            );
                                          }}
                                          placeholder={`Value ${index + 1}`}
                                          className="flex-1"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => {
                                            const updated = splitValues.filter(
                                              (_, i) => i !== index
                                            );
                                            form.setFieldValue(
                                              "category_code_values",
                                              updated.join(",")
                                            );
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4 text-red-500" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="text-center py-2 text-gray-500">
                                    <p>No category code values added yet.</p>
                                  </div>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const updated = [...splitValues, ""];
                                    form.setFieldValue(
                                      "category_code_values",
                                      updated.join(",")
                                    );
                                  }}
                                  className="mt-2"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Value
                                </Button>
                              </div>
                            );
                          }}
                        </FieldArray>
                        <p className="text-xs text-gray-500 mt-1">
                          Add multiple values that will be associated with this
                          category code.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Script */}
                <Card className="border border-gray-200">
                  <CardHeader>
                    <CardTitle>Script</CardTitle>
                    <CardDescription>
                      Customer-specific script for interpreters
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="script">Script</Label>
                      <Tiptap
                        value={values.script}
                        onChange={(content) => setFieldValue("script", content)}
                      />
                      <ErrorMessage
                        name="script"
                        component="div"
                        className="text-xs text-red-500 font-light"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Notes Section */}
                <div className="space-y-4 mt-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Notes</CardTitle>
                      <CardDescription>Customer-specific notes</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Field name="notes">
                        {({ field, form }) => (
                          <Tiptap
                            value={field.value}
                            onChange={(newValue) =>
                              form.setFieldValue("notes", newValue)
                            }
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name="notes"
                        component="div"
                        className="text-sm text-red-500"
                      />
                    </CardContent>
                  </Card>
                </div>

                {/* Custom Attributes */}
                <FieldArray name="attributes">
                  {({ remove, push }) => (
                    <Card className="border border-gray-200">
                      <CardHeader>
                        <div>
                          <CardTitle>Custom Attributes</CardTitle>
                          <CardDescription>
                            Add custom fields to store additional information
                          </CardDescription>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {values.attributes && values.attributes.length > 0 ? (
                          values.attributes.map((attr, idx) => (
                            <div
                              key={idx}
                              className="p-4 border border-gray-200 rounded-lg"
                            >
                              <div className="flex justify-between items-center">
                                <h4 className="font-medium">
                                  Attribute {idx + 1}
                                </h4>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => remove(idx)}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <div className="space-y-2">
                                  <Label htmlFor={`attribute_name_${idx}`}>
                                    Attribute Name *
                                  </Label>
                                  <Field
                                    as={Input}
                                    id={`attribute_name_${idx}`}
                                    value={attr.attribute_name}
                                    name={`attributes[${idx}].attribute_name`}
                                    placeholder="Attribute Name (e.g., Department)"
                                  />
                                  <ErrorMessage
                                    name={`attributes[${idx}].attribute_name`}
                                    component="div"
                                    className="text-sm text-red-500"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`attribute_subtitle_${idx}`}>
                                    Attribute subtitle
                                  </Label>
                                  <Field
                                    as={Input}
                                    id={`attribute_subtitle_${idx}`}
                                    value={attr.attribute_subtitle}
                                    name={`attributes[${idx}].attribute_subtitle`}
                                    placeholder="Attribute subtitle"
                                  />
                                  <ErrorMessage
                                    name={`attributes[${idx}].attribute_subtitle`}
                                    component="div"
                                    className="text-sm text-red-500"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor={`attribute_mode_${idx}`}>
                                    Field Mode
                                  </Label>
                                  <Field
                                    name={`attributes[${idx}].attribute_mode`}
                                  >
                                    {({ field, form }) => (
                                      <Select
                                        value={field.value || "text"}
                                        onValueChange={(value) => {
                                          form.setFieldValue(
                                            `attributes[${idx}].attribute_mode`,
                                            value
                                          );
                                          if (value !== "list") {
                                            form.setFieldValue(
                                              `attributes[${idx}].attribute_options`,
                                              []
                                            );
                                          }
                                        }}
                                      >
                                        <SelectTrigger>
                                          <SelectValue placeholder="Select mode" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="text">
                                            Text
                                          </SelectItem>
                                          <SelectItem value="input">
                                            Input
                                          </SelectItem>
                                          <SelectItem value="list">
                                            List
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    )}
                                  </Field>
                                </div>

                                {attr.attribute_mode === "list" && (
                                  <div className="space-y-2">
                                    <Label>List Values</Label>
                                    <FieldArray
                                      name={`attributes[${idx}].attribute_options`}
                                    >
                                      {({ remove, push }) => (
                                        <div className="flex flex-col">
                                          {Array.isArray(
                                            attr.attribute_options
                                          ) &&
                                          attr.attribute_options.length > 0 ? (
                                            <div className="space-y-2">
                                              {attr.attribute_options.map(
                                                (value, optIdx) => (
                                                  <div
                                                    key={optIdx}
                                                    className="flex items-center gap-2"
                                                  >
                                                    <Field
                                                      as={Input}
                                                      name={`attributes[${idx}].attribute_options.${optIdx}`}
                                                      placeholder={`Value ${
                                                        optIdx + 1
                                                      }`}
                                                      className="flex-1"
                                                    />
                                                    <Button
                                                      type="button"
                                                      variant="ghost"
                                                      size="icon"
                                                      onClick={() =>
                                                        remove(optIdx)
                                                      }
                                                    >
                                                      <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                  </div>
                                                )
                                              )}
                                            </div>
                                          ) : (
                                            <div className="text-center text-sm py-2 text-gray-500">
                                              <p>No values added yet.</p>
                                            </div>
                                          )}
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => push("")}
                                            className="mt-2"
                                          >
                                            <Plus className="h-4 w-4 mr-2" />
                                            Add Value
                                          </Button>
                                        </div>
                                      )}
                                    </FieldArray>
                                  </div>
                                )}

                                <div className="space-y-2">
                                  <div className="flex items-center space-x-2">
                                    <Switch
                                      id={`required-${idx}`}
                                      checked={attr.is_required}
                                      onCheckedChange={(checked) =>
                                        setFieldValue(
                                          `attributes[${idx}].is_required`,
                                          checked
                                        )
                                      }
                                    />
                                    <Label
                                      htmlFor={`required-${idx}`}
                                      className="!mt-0"
                                    >
                                      Required
                                    </Label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-2 text-gray-500">
                            <p>No custom attributes added yet.</p>
                          </div>
                        )}
                        <div className="flex justify-center mt-4">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              push({
                                attribute_name: "",
                                attribute_label: "",
                                attribute_type: "text",
                                attribute_options: "",
                                is_required: false,
                                is_input_type: false,
                                value: "",
                              });
                            }}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            {values.attributes && values.attributes.length > 0
                              ? "Add Another Attribute"
                              : "Add Attribute"}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </FieldArray>
              </TabsContent>
              <TabsContent value="video-portal" className="mt-0 space-y-4">
                <Card className="border border-gray-200">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <div>
                      <CardTitle>Video portal accounts</CardTitle>
                      <CardDescription>
                        Manage video portal accounts and settings for this
                        customer
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={downloadPortalAccountsCSV}
                        disabled={
                          portalUsers.length === 0 ||
                          isDownloadingPortalAccounts
                        }
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {isDownloadingPortalAccounts
                          ? "Downloading..."
                          : "Download CSV"}
                      </Button>
                      <Button
                        type="button"
                        onClick={handlePortalCreateOpen}
                        className="shrink-0"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create account
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-2 pb-4 mb-4 border-b border-gray-200">
                      <Switch
                        id="is_video_enabled"
                        checked={values.is_video_enabled === 1}
                        onCheckedChange={(checked) =>
                          setFieldValue("is_video_enabled", checked ? 1 : 0)
                        }
                      />
                      <Label
                        htmlFor="is_video_enabled"
                        className="text-sm font-medium cursor-pointer"
                      >
                        Video enabled for this customer
                      </Label>
                    </div>
                    {portalUsers.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-6 text-center">
                        No video portal accounts yet. Create a shared or
                        individual account to get started.
                      </p>
                    ) : (
                      <Table
                        rowKey="id"
                        size="small"
                        bordered
                        columns={[
                          {
                            title: "Username",
                            dataIndex: "username",
                            key: "username",
                            render: (val) => val || "—",
                          },
                          {
                            title: "Name",
                            key: "name",
                            render: (_, u) =>
                              u.account_type === "shared"
                                ? "—"
                                : `${u.first_name || ""} ${
                                    u.last_name || ""
                                  }`.trim() || "—",
                          },
                          {
                            title: "Type",
                            dataIndex: "account_type",
                            key: "account_type",
                            render: (account_type) =>
                              account_type === "shared"
                                ? "Shared"
                                : "Individual",
                          },

                          {
                            title: "Active",
                            key: "is_active",
                            render: (_, u) =>
                              u.is_active === 0 ? "No" : "Yes",
                          },
                          {
                            title: "Call History",
                            key: "can_view_call_history",
                            render: (_, u) =>
                              u.can_view_call_history === 0 ||
                              u.can_view_call_history === false
                                ? "No"
                                : "Yes",
                          },
                          {
                            title: "Change Password",
                            key: "can_change_password",
                            render: (_, u) =>
                              u.can_change_password === 0 ||
                              u.can_change_password === false
                                ? "No"
                                : "Yes",
                          },
                          {
                            title: "Actions",
                            key: "actions",
                            width: 100,
                            render: (_, u) => (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handlePortalEditOpen(u)}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit account
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handlePortalResetOpen(u)}
                                  >
                                    <KeyRound className="h-4 w-4 mr-2" />
                                    Reset password
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    variant="destructive"
                                    onClick={() => handlePortalDelete(u)}
                                    className="text-red-600 focus:text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete account
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ),
                          },
                        ]}
                        dataSource={portalUsers}
                        pagination={{
                          current: portalPagination.current,
                          pageSize: portalPagination.pageSize,
                          total: portalPagination.total,
                          showSizeChanger: true,
                          showQuickJumper: true,
                          showTotal: (total) => `${total} records`,
                          pageSizeOptions: ["10", "20", "30", "50", "100"],
                          size: "default",
                          position: ["bottomRight"],
                          onChange: (page, pageSize) => {
                            fetchPortalAccounts(
                              page,
                              pageSize ?? portalPagination.pageSize
                            );
                          },
                          onShowSizeChange: (current, size) => {
                            fetchPortalAccounts(1, size);
                          },
                        }}
                      />
                    )}
                  </CardContent>
                </Card>

                {/* Create portal account dialog */}
                <Dialog
                  open={portalCreateOpen}
                  onOpenChange={(open) => {
                    setPortalCreateOpen(open);
                    if (!open) {
                      setPortalCreateForm({
                        firstName: "",
                        lastName: "",
                        email: "",
                        username: "",
                        organization: "",
                        password: "",
                        confirmPassword: "",
                        can_view_call_history: true,
                        can_change_password: true,
                      });
                      setPortalCreateMode("single");
                      setPortalBulkText("");
                    }
                  }}
                >
                  <DialogContent
                    className="sm:max-w-2xl"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                  >
                    <DialogHeader>
                      <DialogTitle>Create video portal account</DialogTitle>
                      <DialogDescription>
                        Choose shared or individual account, and fill in the
                        details. Password required (8+ chars, upper, lower,
                        number, special @$!%*?&).
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6 py-4">
                      <div className="space-y-3">
                        <Label>Account type</Label>
                        <RadioGroup
                          value={portalAccountType}
                          onValueChange={setPortalAccountType}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="shared" id="portal-shared" />
                            <Label
                              htmlFor="portal-shared"
                              className="font-normal cursor-pointer"
                            >
                              Shared
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="individual"
                              id="portal-individual"
                            />
                            <Label
                              htmlFor="portal-individual"
                              className="font-normal cursor-pointer"
                            >
                              Individual
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="portal-create-call-history">
                            Allow call history access
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Turn off for service-only accounts with no Call
                            History access.
                          </p>
                        </div>
                        <Switch
                          id="portal-create-call-history"
                          checked={portalCreateForm.can_view_call_history}
                          onCheckedChange={(checked) =>
                            setPortalCreateForm((prev) => ({
                              ...prev,
                              can_view_call_history: checked,
                            }))
                          }
                        />
                      </div>

                      <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="portal-create-change-password">
                            Allow password change
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            When off, this account cannot change password on
                            the portal or mobile apps.
                          </p>
                        </div>
                        <Switch
                          id="portal-create-change-password"
                          checked={portalCreateForm.can_change_password}
                          onCheckedChange={(checked) =>
                            setPortalCreateForm((prev) => ({
                              ...prev,
                              can_change_password: checked,
                            }))
                          }
                        />
                      </div>

                      {/* <div className="space-y-3">
                        <Label>Insert mode</Label>
                        <RadioGroup
                          value={portalCreateMode}
                          onValueChange={setPortalCreateMode}
                          className="flex gap-6"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="single"
                              id="portal-mode-single"
                            />
                            <Label
                              htmlFor="portal-mode-single"
                              className="font-normal cursor-pointer"
                            >
                              Single
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="bulk"
                              id="portal-mode-bulk"
                            />
                            <Label
                              htmlFor="portal-mode-bulk"
                              className="font-normal cursor-pointer"
                            >
                              Bulk insert
                            </Label>
                          </div>
                        </RadioGroup>
                      </div> */}

                      {portalCreateMode === "bulk" ? (
                        <div className="space-y-2">
                          <Label htmlFor="portal-bulk">
                            {portalAccountType === "shared"
                              ? "One username,password per line (comma or tab-separated)"
                              : "One account per line (comma or tab-separated)"}
                          </Label>
                          {portalAccountType === "individual" && (
                            <p className="text-xs text-muted-foreground">
                              Columns: firstName, lastName, email, username,
                              organization (free text), password. Password: 8+
                              chars, upper, lower, number, special (@$!%*?&).
                            </p>
                          )}
                          {portalAccountType === "shared" && (
                            <p className="text-xs text-muted-foreground">
                              Password: 8+ chars, upper, lower, number, special
                              (@$!%*?&).
                            </p>
                          )}
                          <Textarea
                            id="portal-bulk"
                            value={portalBulkText}
                            onChange={(e) => setPortalBulkText(e.target.value)}
                            placeholder={
                              portalAccountType === "shared"
                                ? "shared-user-1,Secret123!\nshared-user-2,Secret456!"
                                : "John,Doe,john@example.com,johndoe,org-a,Secret123!\nJane,Smith,jane@example.com,janesmith,current,Secret456!"
                            }
                            rows={8}
                            className="font-mono text-sm"
                          />
                        </div>
                      ) : portalAccountType === "shared" ? (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="portal-username-shared">
                              Username *
                            </Label>
                            <Input
                              id="portal-username-shared"
                              value={portalCreateForm.username}
                              onChange={(e) =>
                                setPortalCreateForm((prev) => ({
                                  ...prev,
                                  username: e.target.value,
                                }))
                              }
                              placeholder="Username for shared account"
                            />
                          </div>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="portal-password-shared">
                                Password *
                              </Label>
                              <Input
                                id="portal-password-shared"
                                type="password"
                                value={portalCreateForm.password}
                                onChange={(e) =>
                                  setPortalCreateForm((prev) => ({
                                    ...prev,
                                    password: e.target.value,
                                  }))
                                }
                                placeholder="Password"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="portal-confirm-shared">
                                Confirm password *
                              </Label>
                              <Input
                                id="portal-confirm-shared"
                                type="password"
                                value={portalCreateForm.confirm_password}
                                onChange={(e) =>
                                  setPortalCreateForm((prev) => ({
                                    ...prev,
                                    confirm_password: e.target.value,
                                  }))
                                }
                                placeholder="Confirm password"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            8+ chars, upper, lower, number, special (@$!%*?&);
                            must match.
                          </p>
                        </div>
                      ) : (
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="portal-firstName">
                              First name *
                            </Label>
                            <Input
                              id="portal-firstName"
                              value={portalCreateForm.first_name}
                              onChange={(e) =>
                                setPortalCreateForm((prev) => ({
                                  ...prev,
                                  first_name: e.target.value,
                                }))
                              }
                              placeholder="First name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="portal-lastName">Last name *</Label>
                            <Input
                              id="portal-lastName"
                              value={portalCreateForm.last_name}
                              onChange={(e) =>
                                setPortalCreateForm((prev) => ({
                                  ...prev,
                                  last_name: e.target.value,
                                }))
                              }
                              placeholder="Last name"
                            />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="portal-email">Email *</Label>
                            <Input
                              id="portal-email"
                              type="email"
                              value={portalCreateForm.email}
                              onChange={(e) =>
                                setPortalCreateForm((prev) => ({
                                  ...prev,
                                  email: e.target.value,
                                }))
                              }
                              placeholder="Email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="portal-username">Username *</Label>
                            <Input
                              id="portal-username"
                              value={portalCreateForm.username}
                              onChange={(e) =>
                                setPortalCreateForm((prev) => ({
                                  ...prev,
                                  username: e.target.value,
                                }))
                              }
                              placeholder="Username"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="portal-organization">
                              Organization (optional)
                            </Label>
                            <Input
                              id="portal-organization"
                              value={portalCreateForm.organization_name}
                              onChange={(e) =>
                                setPortalCreateForm((prev) => ({
                                  ...prev,
                                  organization_name: e.target.value,
                                }))
                              }
                              placeholder="Organization"
                            />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label>Password *</Label>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <Input
                                type="password"
                                value={portalCreateForm.password}
                                onChange={(e) =>
                                  setPortalCreateForm((prev) => ({
                                    ...prev,
                                    password: e.target.value,
                                  }))
                                }
                                placeholder="Password"
                              />
                              <Input
                                type="password"
                                value={portalCreateForm.confirm_password}
                                onChange={(e) =>
                                  setPortalCreateForm((prev) => ({
                                    ...prev,
                                    confirm_password: e.target.value,
                                  }))
                                }
                                placeholder="Confirm password"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              8+ chars, upper, lower, number, special (@$!%*?&);
                              must match.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setPortalCreateOpen(false)}
                        disabled={portalCreateSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handlePortalCreate}
                        disabled={portalCreateSubmitting}
                      >
                        {portalCreateSubmitting
                          ? portalCreateMode === "bulk"
                            ? "Creating accounts..."
                            : "Creating..."
                          : portalCreateMode === "bulk"
                          ? "Create accounts"
                          : "Create account"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Edit video portal account dialog */}
                <Dialog
                  open={portalEditOpen}
                  onOpenChange={(open) => {
                    setPortalEditOpen(open);
                    if (!open) {
                      setPortalEditUser(null);
                    }
                  }}
                >
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Edit video portal account</DialogTitle>
                      <DialogDescription>
                        Update username, name, email, phone, organization, and
                        active status.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="portal-edit-username">Username</Label>
                        <Input
                          id="portal-edit-username"
                          value={portalEditForm.username}
                          onChange={(e) =>
                            setPortalEditForm((prev) => ({
                              ...prev,
                              username: e.target.value,
                            }))
                          }
                          placeholder="Username"
                        />
                      </div>
                      {portalEditUser?.account_type === "individual" && (
                        <>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-2">
                              <Label htmlFor="portal-edit-firstName">
                                First name
                              </Label>
                              <Input
                                id="portal-edit-firstName"
                                value={portalEditForm.first_name}
                                onChange={(e) =>
                                  setPortalEditForm((prev) => ({
                                    ...prev,
                                    first_name: e.target.value,
                                  }))
                                }
                                placeholder="First name"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="portal-edit-lastName">
                                Last name
                              </Label>
                              <Input
                                id="portal-edit-lastName"
                                value={portalEditForm.last_name}
                                onChange={(e) =>
                                  setPortalEditForm((prev) => ({
                                    ...prev,
                                    last_name: e.target.value,
                                  }))
                                }
                                placeholder="Last name"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="portal-edit-email">Email</Label>
                            <Input
                              id="portal-edit-email"
                              type="email"
                              value={portalEditForm.email}
                              onChange={(e) =>
                                setPortalEditForm((prev) => ({
                                  ...prev,
                                  email: e.target.value,
                                }))
                              }
                              placeholder="Email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="portal-edit-phone">
                              Phone number
                            </Label>
                            <Input
                              id="portal-edit-phone"
                              value={portalEditForm.phone_number}
                              onChange={(e) =>
                                setPortalEditForm((prev) => ({
                                  ...prev,
                                  phone_number: e.target.value,
                                }))
                              }
                              placeholder="Phone number"
                            />
                          </div>
                        </>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="portal-edit-organization">
                          Organization
                        </Label>
                        <Input
                          id="portal-edit-organization"
                          value={portalEditForm.organization_name}
                          onChange={(e) =>
                            setPortalEditForm((prev) => ({
                              ...prev,
                              organization_name: e.target.value,
                            }))
                          }
                          placeholder="Organization"
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="portal-edit-call-history">
                            Allow call history access
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            When off, this account cannot view Call History on
                            the portal or mobile apps.
                          </p>
                        </div>
                        <Switch
                          id="portal-edit-call-history"
                          checked={Boolean(portalEditForm.can_view_call_history)}
                          onCheckedChange={(checked) =>
                            setPortalEditForm((prev) => ({
                              ...prev,
                              can_view_call_history: checked,
                            }))
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="portal-edit-change-password">
                            Allow password change
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            When off, this account cannot change password on
                            the portal or mobile apps.
                          </p>
                        </div>
                        <Switch
                          id="portal-edit-change-password"
                          checked={Boolean(portalEditForm.can_change_password)}
                          onCheckedChange={(checked) =>
                            setPortalEditForm((prev) => ({
                              ...prev,
                              can_change_password: checked,
                            }))
                          }
                        />
                      </div>
                      <div className="flex items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <Label htmlFor="portal-edit-active">
                            Account active
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Deactivated accounts cannot sign in.
                          </p>
                        </div>
                        <Switch
                          id="portal-edit-active"
                          checked={Boolean(portalEditForm.is_active)}
                          onCheckedChange={(checked) =>
                            setPortalEditForm((prev) => ({
                              ...prev,
                              is_active: checked,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setPortalEditOpen(false);
                          setPortalEditUser(null);
                        }}
                        disabled={portalEditSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handlePortalEditSave}
                        disabled={portalEditSubmitting}
                      >
                        {portalEditSubmitting ? "Saving..." : "Save"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Reset portal user password dialog */}
                <Dialog
                  open={portalResetOpen}
                  onOpenChange={(open) => {
                    setPortalResetOpen(open);
                    if (!open) {
                      setPortalResetUser(null);
                      setPortalNewPassword("");
                      setPortalConfirmPassword("");
                      setPortalPasswordError("");
                    }
                  }}
                >
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Reset password</DialogTitle>
                      <DialogDescription>
                        {portalResetUser?.type === "shared"
                          ? "Set a new password for this shared account."
                          : `Set a new password for ${
                              portalResetUser?.first_name || ""
                            } ${portalResetUser?.last_name || ""}`.trim() ||
                            "this account."}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="portal-new-password">
                          New password *
                        </Label>
                        <Input
                          id="portal-new-password"
                          type="password"
                          value={portalNewPassword}
                          onChange={(e) => {
                            setPortalNewPassword(e.target.value);
                            checkPortalPasswordRequirements(
                              e.target.value,
                              portalConfirmPassword
                            );
                          }}
                          placeholder="New password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="portal-confirm-password">
                          Confirm password *
                        </Label>
                        <Input
                          id="portal-confirm-password"
                          type="password"
                          value={portalConfirmPassword}
                          onChange={(e) => {
                            setPortalConfirmPassword(e.target.value);
                            checkPortalPasswordRequirements(
                              portalNewPassword,
                              e.target.value
                            );
                          }}
                          placeholder="Confirm password"
                        />
                      </div>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li
                          className={
                            portalPasswordRequirements.length
                              ? "text-green-600"
                              : ""
                          }
                        >
                          {portalPasswordRequirements.length ? (
                            <Check className="inline h-3 w-3 mr-1" />
                          ) : (
                            <X className="inline h-3 w-3 mr-1" />
                          )}
                          At least 8 characters
                        </li>
                        <li
                          className={
                            portalPasswordRequirements.uppercase &&
                            portalPasswordRequirements.lowercase &&
                            portalPasswordRequirements.number &&
                            portalPasswordRequirements.special
                              ? "text-green-600"
                              : ""
                          }
                        >
                          {portalPasswordRequirements.uppercase &&
                          portalPasswordRequirements.lowercase &&
                          portalPasswordRequirements.number &&
                          portalPasswordRequirements.special ? (
                            <Check className="inline h-3 w-3 mr-1" />
                          ) : (
                            <X className="inline h-3 w-3 mr-1" />
                          )}
                          One uppercase, one lowercase, one number, one special
                          (@$!%*?&)
                        </li>
                        <li
                          className={
                            portalPasswordRequirements.match
                              ? "text-green-600"
                              : ""
                          }
                        >
                          {portalPasswordRequirements.match ? (
                            <Check className="inline h-3 w-3 mr-1" />
                          ) : (
                            <X className="inline h-3 w-3 mr-1" />
                          )}
                          Passwords match
                        </li>
                      </ul>
                      {portalPasswordError && (
                        <p className="text-sm text-red-500">
                          {portalPasswordError}
                        </p>
                      )}
                    </div>
                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setPortalResetOpen(false);
                          setPortalResetUser(null);
                          setPortalNewPassword("");
                          setPortalConfirmPassword("");
                          setPortalPasswordError("");
                        }}
                        disabled={portalResetSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button"
                        onClick={handlePortalResetPassword}
                        disabled={portalResetSubmitting}
                      >
                        {portalResetSubmitting
                          ? "Resetting..."
                          : "Reset password"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Delete portal account confirmation */}
                <AlertDialog
                  open={portalDeleteOpen}
                  onOpenChange={(open) => {
                    setPortalDeleteOpen(open);
                    if (!open) setPortalDeleteUser(null);
                  }}
                >
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Delete video portal account
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this account?
                        {portalDeleteUser?.type === "individual" &&
                          ` This will remove ${
                            portalDeleteUser?.first_name || ""
                          } ${portalDeleteUser?.last_name || ""}`.trim()}
                        . This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handlePortalDeleteConfirm}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TabsContent>
            </Tabs>
            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Customer</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this customer? This action
                    cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(false)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      try {
                        setIsDeleting(true);
                        await delete_customer(customer_id);
                        setToastOpen(true);
                        setToastMessage("Customer deleted successfully");
                        setToastSeverity("success");
                        navigate("/admin/customers");
                      } catch (error) {
                        console.error("Error deleting customer:", error);
                        setToastOpen(true);
                        setToastMessage("Failed to delete customer");
                        setToastSeverity("error");
                      } finally {
                        setIsDeleting(false);
                        setDeleteDialogOpen(false);
                      }
                    }}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditCustomerForm;
